import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getProfile, StudentProfile } from '@/services/student/profile.service';
import { apiResetAllModuleProgress } from '@/services/student/module-progress.api';
import { ProfileForm } from './components/ProfileForm';
import { ChangePasswordForm } from './components/ChangePasswordForm';
import { SubscriptionGatewayCard } from './components/SubscriptionGatewayCard';
import { useAppToast } from '@/hooks/useAppToast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const GLOBAL_RESET_CONFIRM_PHRASE = 'REINICIAR';

export const SettingsPage = () => {
  const toast = useAppToast();
  const queryClient = useQueryClient();
  const [profile, setProfile] = useState<StudentProfile | undefined>();
  const [globalResetOpen, setGlobalResetOpen] = useState(false);
  const [globalResetInput, setGlobalResetInput] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['student-profile'],
    queryFn: () => getProfile(),
    retry: 1,
  });

  useEffect(() => {
    if (data) {
      setProfile(data);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      console.error('Error loading profile:', error);
      toast.error('Erro ao carregar perfil');
    }
  }, [error, toast]);

  const handleProfileUpdateSuccess = () => {
    refetch();
  };

  const resetAllMutation = useMutation({
    mutationFn: apiResetAllModuleProgress,
    onSuccess: async () => {
      toast.success(
        'Todo o progresso de exercícios dos módulos foi reiniciado.'
      );
      setGlobalResetOpen(false);
      setGlobalResetInput('');
      await queryClient.invalidateQueries({ queryKey: ['module-progress'] });
      await queryClient.invalidateQueries({ queryKey: ['student-profile'] });
    },
    onError: () => {
      toast.error(
        'Não foi possível reiniciar o progresso. Tente novamente mais tarde.'
      );
    },
  });

  const canSubmitGlobalReset =
    globalResetInput.trim() === GLOBAL_RESET_CONFIRM_PHRASE;

  return (
    <div className="container mx-auto p-4 max-w-4xl">
      <h1 className="text-3xl font-bold mb-6">Configurações</h1>
      
      <div className="space-y-6">
        {isLoading ? (
          <div className="flex items-center justify-center min-h-[200px] rounded-lg border border-border">
            <p className="text-muted-foreground">Carregando...</p>
          </div>
        ) : (
          <ProfileForm 
            profile={profile} 
            onSuccess={handleProfileUpdateSuccess}
          />
        )}
        
        <ChangePasswordForm 
          userEmail={profile?.email}
        />

        <SubscriptionGatewayCard />

        <section className="rounded-lg border border-border p-6 space-y-4">
          <h2 className="text-xl font-semibold">Opções avançadas</h2>
          <p className="text-sm text-muted-foreground">
            Reinicia todas as respostas e o progresso dos exercícios em todos os
            módulos. Não altera testes de nível, testes de módulo nem
            desbloqueios.
          </p>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              setGlobalResetInput('');
              setGlobalResetOpen(true);
            }}
          >
            Reiniciar todo o progresso
          </Button>
        </section>
      </div>

      <Dialog
        open={globalResetOpen}
        onOpenChange={(open) => {
          setGlobalResetOpen(open);
          if (!open) {
            setGlobalResetInput('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reiniciar todo o progresso?</DialogTitle>
            <DialogDescription>
              Esta ação apaga respostas e progresso de exercícios em todos os
              módulos da sua conta. Digite{' '}
              <span className="font-mono font-semibold">
                {GLOBAL_RESET_CONFIRM_PHRASE}
              </span>{' '}
              para confirmar.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Label htmlFor="global-reset-confirm">Confirmação</Label>
            <Input
              id="global-reset-confirm"
              autoComplete="off"
              value={globalResetInput}
              onChange={(e) => setGlobalResetInput(e.target.value)}
              placeholder={GLOBAL_RESET_CONFIRM_PHRASE}
              disabled={resetAllMutation.isPending}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setGlobalResetOpen(false)}
              disabled={resetAllMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={!canSubmitGlobalReset || resetAllMutation.isPending}
              onClick={() => resetAllMutation.mutate()}
            >
              {resetAllMutation.isPending ? 'Reiniciando…' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

